import QRCode from '@pancakeswap/ui-wallets/src/components/QRCode'
import { guideHref } from 'components/GuideLink'
import { CSSProperties, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { fmtBnb, needHeadline, usdText } from './logic'
import { useNoBnbPresence } from './presence'

const glow = keyframes`0%, 100% { box-shadow: 0 0 0 0 rgba(240, 185, 11, 0.45), 0 10px 24px -10px rgba(240, 185, 11, 0.8); } 50% { box-shadow: 0 0 0 6px rgba(240, 185, 11, 0), 0 10px 24px -10px rgba(240, 185, 11, 0.8); }`

const Card = styled.section`
  position: relative;
  display: grid;
  gap: 12px;
  width: 100%;
  min-width: 0;
  padding: 16px;
  border-radius: 20px;
  text-align: left;
  color: ${({ theme }) => theme.colors.text};
  background: radial-gradient(120% 120% at 0% 0%, rgba(240, 185, 11, 0.2), transparent 55%),
    ${({ theme }) => (theme.isDark ? 'linear-gradient(160deg, rgba(36, 28, 6, 0.92), rgba(13, 21, 48, 0.94))' : 'linear-gradient(160deg, #fff8e1, #ffffff)')};
  border: 1px solid rgba(240, 185, 11, 0.55);
  box-shadow: 0 16px 36px -22px rgba(240, 185, 11, 0.65);
`
const Head = styled.div`
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
`
const Coin = styled.span`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: #14151a;
  border: 2px solid rgba(240, 185, 11, 0.85);
  animation: ${glow} 2.4s ease-in-out infinite;
  img {
    width: 36px;
    height: 36px;
    border-radius: 50%;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`
const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  line-height: 22px;
  font-weight: 800;
  letter-spacing: -0.005em;
`
const Sub = styled.p`
  margin: 2px 0 0;
  font-size: 13px;
  line-height: 18px;
  color: ${({ theme }) => theme.colors.textSubtle};
  overflow-wrap: anywhere;
`
const Option = styled.div`
  display: grid;
  gap: 8px;
  padding: 12px;
  border-radius: 16px;
  min-width: 0;
  background: ${({ theme }) => (theme.isDark ? 'rgba(5, 7, 19, 0.55)' : 'rgba(240, 185, 11, 0.06)')};
  border: 1px solid ${({ theme }) => (theme.isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(240, 185, 11, 0.25)')};
  strong {
    font-size: 14px;
    line-height: 20px;
    font-weight: 800;
  }
  p {
    margin: 0;
    font-size: 13px;
    line-height: 18px;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
  b {
    color: ${({ theme }) => theme.colors.text};
  }
`
const Addr = styled.code`
  display: block;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0.01em;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => (theme.isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.05)')};
`
/** Copy and QR: twins of the same width. */
const Twins = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`
const Btn = styled.button`
  min-height: 44px;
  padding: 0 12px;
  border-radius: 12px;
  font: inherit;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.text};
  background: transparent;
  border: 1px solid rgba(240, 185, 11, 0.55);
  &:focus-visible {
    outline: 2px solid #f0b90b;
    outline-offset: 2px;
  }
`
const Qr = styled.div`
  justify-self: center;
  width: 176px;
  padding: 6px;
  border-radius: 14px;
  background: #fff;
  svg {
    display: block;
    width: 164px;
    height: 164px;
  }
`
const Foot = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 12px;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.textSubtle};
  a {
    color: ${({ theme }) => theme.colors.primary};
    font-size: 14px;
    font-weight: 700;
    min-height: 44px;
    display: inline-flex;
    align-items: center;
  }
`

export interface NoBnbPanelProps {
  address: string
  gasWei: bigint
  /** BNB the step itself sends (a lock fee, a competition fee); 0 for a plain swap */
  extraWei: bigint
  /** what the extra BNB is for, e.g. "the lock fee" */
  extraLabel?: string
  haveWei?: bigint
  bnbUsd?: number
  /** balance = the read balance is below the need; error = the wallet refused for lack of BNB */
  reason: 'balance' | 'error'
  testId?: string
  style?: CSSProperties
}

/**
 * The friendly answer to "not enough BNB for gas": how much (BNB and USD), and how to get it to THIS address: buy BNB on an exchange,
 * withdraw it on BNB Smart Chain (BEP20) with a copy button and a QR, then come back and continue; plus the docs guide. No card option:
 * the fiat on-ramp left the app on 2026-10-07 (Transak declined, archive/onramp-20261007). It never promises free gas: every
 * transaction pays its own network fee and CADINU does not pay it.
 */
export default function NoBnbPanel({ address, gasWei, extraWei, extraLabel, haveWei, bnbUsd, reason, testId = 'no-bnb-panel', style }: NoBnbPanelProps) {
  const [copied, setCopied] = useState(false)
  const [qr, setQr] = useState(false)
  useNoBnbPresence(true) // the fixed profile bar steps aside while this panel is on the page
  const total = gasWei + extraWei
  const withFee = extraWei > 0n
  const usd = usdText(total, bnbUsd)
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(address)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch {
      /* no clipboard: the address is on screen to select */
    }
  }
  return (
    <Card style={style} data-testid={testId} data-reason={reason} role={reason === 'error' ? 'alert' : 'region'} aria-label="Not enough BNB for network fees">
      <Head>
        <Coin>
          <img src="/images/chains/56.png" alt="" width={36} height={36} />
        </Coin>
        <div style={{ minWidth: 0 }}>
          <Title>{needHeadline(usd, withFee)}</Title>
          <Sub>
            {withFee
              ? `About ${fmtBnb(total)}: ${extraLabel ?? 'the BNB this step sends'} (${fmtBnb(extraWei)}) plus network fees (${fmtBnb(gasWei)}).`
              : `About ${fmtBnb(total)}. Every transaction on BNB Smart Chain pays a small network fee in BNB.`}
            {haveWei !== undefined && ` This wallet has ${fmtBnb(haveWei)}.`}
            {reason === 'error' && ' Your wallet stopped the transaction because the BNB was not enough. Nothing was sent.'}
          </Sub>
        </div>
      </Head>

      <Option>
        <strong>Get BNB from an exchange</strong>
        <p>
          Buy <b>BNB</b> on Binance or any exchange you use, then withdraw it to this address and pick the network <b>BNB Smart Chain (BEP20)</b>. Another network sends the coins somewhere this wallet cannot see.
        </p>
        <Addr data-testid="no-bnb-address">{address}</Addr>
        <Twins>
          <Btn type="button" onClick={copy} data-testid="no-bnb-copy" aria-label="Copy your wallet address">
            {copied ? 'Copied' : 'Copy address'}
          </Btn>
          <Btn type="button" onClick={() => setQr(!qr)} aria-expanded={qr} data-testid="no-bnb-qr-toggle">
            {qr ? 'Hide QR' : 'Show QR'}
          </Btn>
        </Twins>
        {qr && (
          <Qr data-testid="no-bnb-qr" aria-label="QR code of your wallet address">
            <QRCode url={address} />
          </Qr>
        )}
        <p data-testid="no-bnb-next">When it arrives, come back and continue. On Swap, BNB swaps for any other token.</p>
      </Option>

      <Foot>
        <span>CADINU does not pay network fees for you. The amount is an estimate from the current gas price.</span>
        <a href={guideHref('bnbFees')} target="_blank" rel="noopener noreferrer" data-testid="no-bnb-guide">
          How to get BNB
        </a>
      </Foot>
    </Card>
  )
}
